import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Typography,
  Card,
  Input,
  Modal,
  Drawer,
  Form,
  Select,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Tag,
  Tabs,
  Divider,
  Badge,
  Avatar,
  FloatButton,
  Grid,
  DatePicker,
  InputNumber,
  Alert,
  Progress,
  Tooltip
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ContactsOutlined,
  UserOutlined,
  TeamOutlined,
  ShopOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  EyeOutlined,
  FilterOutlined,
  MoreOutlined,
  SafetyOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  DollarOutlined,
  ReloadOutlined,
  WarningOutlined
} from '@ant-design/icons'
import contactService from '../../services/contactService'
import ContactDashboard from './ContactDashboard'
import ContactProfileDetails from './ContactProfileDetails'
import api from '../../utils/api'
import dayjs from 'dayjs'
import 'dayjs/locale/th'

const { Title } = Typography
const { Search } = Input
const { Option } = Select
const { useBreakpoint } = Grid
const { RangePicker } = DatePicker

dayjs.locale('th')

const ACCOUNT_ID_FIELDS = [
  'receivable_account_id',
  'payable_account_id',
  'revenue_account_id',
  'expense_account_id'
]

const normalizeAccountId = (value) => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  const numericValue = Number(value)
  return Number.isNaN(numericValue) ? value : numericValue
}

const normalizeAccountOption = (account) => {
  const value = normalizeAccountId(
    account?.id ??
    account?.account_id ??
    account?.accountId ??
    account?.ID
  )

  const code =
    account?.CODE ??
    account?.account_code ??
    account?.code ??
    ''

  const name =
    account?.NAME ??
    account?.account_name ??
    account?.name ??
    ''

  const allowPosting =
    account?.allow_posting === undefined ||
    account?.allow_posting === null ||
    Number(account.allow_posting) !== 0

  const isActive =
    account?.is_active === undefined ||
    account?.is_active === null ||
    Number(account.is_active) !== 0

  if (value === undefined || !code || !name || !allowPosting || !isActive) {
    return null
  }

  return {
    value,
    label: `${code} - ${name}`
  }
}

const normalizeContactAccountFields = (contact = {}) => ({
  receivable_account_id: normalizeAccountId(
    contact.receivable_account_id ??
    contact.RECEIVABLE_ACCOUNT_ID ??
    contact.receivableAccountId
  ),
  payable_account_id: normalizeAccountId(
    contact.payable_account_id ??
    contact.PAYABLE_ACCOUNT_ID ??
    contact.payableAccountId
  ),
  revenue_account_id: normalizeAccountId(
    contact.revenue_account_id ??
    contact.REVENUE_ACCOUNT_ID ??
    contact.revenueAccountId
  ),
  expense_account_id: normalizeAccountId(
    contact.expense_account_id ??
    contact.EXPENSE_ACCOUNT_ID ??
    contact.expenseAccountId
  )
})

const ContactsPage = () => {
  const [contacts, setContacts] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [renewalModalVisible, setRenewalModalVisible] = useState(false)
  const [editingContact, setEditingContact] = useState(null)
  const [viewingContact, setViewingContact] = useState(null)
  const [renewingContact, setRenewingContact] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [selectedInsuranceStatus, setSelectedInsuranceStatus] = useState('')
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })
  const [typeCounts, setTypeCounts] = useState([])
  const [insuranceStatusCounts, setInsuranceStatusCounts] = useState({})
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false)
  const [expiringContacts, setExpiringContacts] = useState([])
  const [accountOptions, setAccountOptions] = useState([])
  const [form] = Form.useForm()
  const [renewalForm] = Form.useForm()

  // Get screen breakpoints
  const screens = useBreakpoint()
  const isMobile = !screens.md

  // Contact types
  const contactTypes = [
    { value: 'customer', label: 'ลูกค้า', icon: <UserOutlined />, color: 'blue' },
    { value: 'supplier', label: 'ซัพพลายเออร์', icon: <ShopOutlined />, color: 'green' },
    { value: 'both', label: 'ทั้งสองประเภท', icon: <TeamOutlined />, color: 'purple' }
  ]

  // Insurance status types
  const insuranceStatusTypes = [
    { value: 'active', label: 'ใช้งานได้', icon: <CheckCircleOutlined />, color: 'green' },
    { value: 'expiring_soon', label: 'ใกล้หมดอายุ', icon: <WarningOutlined />, color: 'orange' },
    { value: 'expired', label: 'หมดอายุแล้ว', icon: <ExclamationCircleOutlined />, color: 'red' },
    { value: 'not_applicable', label: 'ไม่มีประกัน', icon: <ClockCircleOutlined />, color: 'gray' }
  ]

  useEffect(() => {
    fetchContacts()
    fetchExpiringContacts()
  }, [pagination.current, pagination.pageSize, searchTerm, selectedType, selectedInsuranceStatus])

  useEffect(() => {
    let mounted = true

    const fetchAccountOptions = async () => {
      try {
        const response = await api.get('/api/accounting/accounts')
        const accountData = Array.isArray(response?.data?.data)
          ? response.data.data
          : []

        const options = accountData
          .map(normalizeAccountOption)
          .filter(Boolean)

        if (mounted) {
          setAccountOptions(options)
        }
      } catch (error) {
        console.error('Failed to fetch accounting accounts:', error)

        if (mounted) {
          setAccountOptions([])
        }
      }
    }

    fetchAccountOptions()

    return () => {
      mounted = false
    }
  }, [])

  const fetchContacts = async () => {
    setLoading(true)
    try {
      const result = await contactService.getAllContacts({
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchTerm,
        type: selectedType,
        insurance_status: selectedInsuranceStatus
      })

      setContacts(result.data)
      setPagination(prev => ({
        ...prev,
        total: result.pagination.total || 0
      }))
      setTypeCounts(result.typeCounts)
      setInsuranceStatusCounts(result.insuranceStatusCounts || {})
    } catch (error) {
      message.error('ไม่สามารถโหลดข้อมูลผู้ติดต่อได้')
    } finally {
      setLoading(false)
    }
  }

  const fetchExpiringContacts = async () => {
    try {
      const result = await contactService.getExpiringInsurance(30)
      setExpiringContacts(result.data || [])
    } catch (error) {
      console.error('Failed to fetch expiring contacts:', error)
    }
  }

  const handleAddContact = () => {
    setEditingContact(null)
    setModalVisible(true)
    form.resetFields()
  }

  const handleEditContact = (contact) => {
    setEditingContact(contact)

    // ทำให้ account_id ในข้อมูลผู้ติดต่อเป็นชนิดเดียวกับ value ของ Select
    // เพื่อให้ Ant Design จับคู่ค่าเดิมกับ Label ได้ถูกต้อง
    const formData = {
      ...contact,
      ...normalizeContactAccountFields(contact)
    }

    if (contact.insurance_start_date) {
      formData.insurance_start_date = dayjs(contact.insurance_start_date)
    }
    if (contact.insurance_end_date) {
      formData.insurance_end_date = dayjs(contact.insurance_end_date)
    }

    form.resetFields()
    form.setFieldsValue(formData)
    setModalVisible(true)
  }

  const handleRenewInsurance = (contact) => {
    setRenewingContact(contact)
    setRenewalModalVisible(true)

    // Set default renewal dates (1 year from current end date or today)
    const currentEndDate = contact.insurance_end_date ? dayjs(contact.insurance_end_date) : dayjs()
    const newStartDate = currentEndDate.isAfter(dayjs()) ? currentEndDate : dayjs()
    const newEndDate = newStartDate.add(1, 'year')

    renewalForm.setFieldsValue({
      new_start_date: newStartDate,
      new_end_date: newEndDate,
      renewal_amount: contact.insurance_renewal_amount || 0
    })
  }

  const handleViewContact = async (contact) => {
    setViewingContact(contact)
    setDetailModalVisible(true)

    // Fetch complete profile data. Transaction history is owned by ContactDashboard.
    try {
      const detailedContact = await contactService.getContactById(contact.id)
      setViewingContact(detailedContact)
    } catch (error) {
      message.error('ไม่สามารถโหลดรายละเอียดผู้ติดต่อได้')
    }
  }

  const handleDeleteContact = async (id) => {
    try {
      await contactService.deleteContact(id)
      message.success('ลบผู้ติดต่อสำเร็จ')
      fetchContacts()
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message)
      } else {
        message.error('ไม่สามารถลบผู้ติดต่อได้')
      }
    }
  }

  const handleModalOk = async (values) => {
    try {
      // Format dates for API
      const formattedValues = { ...values }
      if (values.insurance_start_date) {
        formattedValues.insurance_start_date = values.insurance_start_date.format('YYYY-MM-DD')
      }
      if (values.insurance_end_date) {
        formattedValues.insurance_end_date = values.insurance_end_date.format('YYYY-MM-DD')
      }

      // ส่ง account_id เป็น Number ให้ตรงกับ Primary Key ของบัญชี
      // หากผู้ใช้ล้างค่า Select ให้ส่ง null แทน undefined
      ACCOUNT_ID_FIELDS.forEach(fieldName => {
        formattedValues[fieldName] = normalizeAccountId(values[fieldName]) ?? null
      })

      if (editingContact) {
        await contactService.updateContact(editingContact.id, formattedValues)
        message.success('อัปเดตผู้ติดต่อสำเร็จ')
      } else {
        await contactService.createContact(formattedValues)
        message.success('เพิ่มผู้ติดต่อสำเร็จ')
      }

      setModalVisible(false)
      form.resetFields()
      fetchContacts()
      fetchExpiringContacts()

    } catch (error) {
      if (error.errorFields) {
        message.error('กรุณากรอกข้อมูลให้ครบถ้วน')
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message)
      } else {
        message.error('ไม่สามารถบันทึกข้อมูลได้')
      }
    }
  }

  const handleRenewalOk = async (values) => {
    try {
      const formattedValues = {
        new_start_date: values.new_start_date.format('YYYY-MM-DD'),
        new_end_date: values.new_end_date.format('YYYY-MM-DD'),
        renewal_amount: values.renewal_amount,
        notes: values.notes
      }

      await contactService.renewInsurance(renewingContact.id, formattedValues)
      message.success('ต่ออายุประกันสำเร็จ')

      setRenewalModalVisible(false)
      renewalForm.resetFields()
      fetchContacts()
      fetchExpiringContacts()

    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message)
      } else {
        message.error('ไม่สามารถต่ออายุประกันได้')
      }
    }
  }

  const handleOk = () => {
    form.submit()
  }

  const handleRenewalSubmit = () => {
    renewalForm.submit()
  }

  const handleModalCancel = () => {
    setModalVisible(false)
    form.resetFields()
  }

  const handleRenewalCancel = () => {
    setRenewalModalVisible(false)
    renewalForm.resetFields()
  }

  const handleSearch = (value) => {
    setSearchTerm(value)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleTypeFilter = (type) => {
    setSelectedType(type)
    setPagination(prev => ({ ...prev, current: 1 }))
    if (isMobile) {
      setFilterDrawerVisible(false)
    }
  }

  const handleInsuranceStatusFilter = (status) => {
    setSelectedInsuranceStatus(status)
    setPagination(prev => ({ ...prev, current: 1 }))
    if (isMobile) {
      setFilterDrawerVisible(false)
    }
  }

  const handleTableChange = (paginationInfo) => {
    setPagination(paginationInfo)
  }

  // Helper functions
  const getInsuranceStatusConfig = (status) => {
    return insuranceStatusTypes.find(s => s.value === status) ||
           { label: status, color: 'default', icon: <ClockCircleOutlined /> }
  }

  const calculateDaysUntilExpiry = (endDate) => {
    if (!endDate) return null
    const today = dayjs()
    const expiry = dayjs(endDate)
    return expiry.diff(today, 'day')
  }

  const renderInsuranceStatus = (contact) => {
    const status = contact.calculated_insurance_status || contact.insurance_status
    const config = getInsuranceStatusConfig(status)
    const daysUntilExpiry = calculateDaysUntilExpiry(contact.insurance_end_date)

    if (status === 'expiring_soon' && daysUntilExpiry !== null) {
      return (
        <Tooltip title={`เหลือ ${daysUntilExpiry} วัน`}>
          <Tag color={config.color} icon={config.icon}>
            {config.label} ({daysUntilExpiry} วัน)
          </Tag>
        </Tooltip>
      )
    }

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.label}
      </Tag>
    )
  }

  // Calculate statistics
  const totalContacts = pagination.total || 0
  const customerCount = typeCounts.find(t => t.type === 'customer')?.count || 0
  const supplierCount = typeCounts.find(t => t.type === 'supplier')?.count || 0
  const bothCount = typeCounts.find(t => t.type === 'both')?.count || 0

  // Desktop table columns
  const columns = [
    {
      title: 'ชื่อ',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {record.company_name && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.company_name}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'ประเภท',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => {
        const typeData = contactTypes.find(t => t.value === type)
        return (
          <Tag color={typeData?.color} icon={typeData?.icon}>
            {typeData?.label}
          </Tag>
        )
      }
    },
    {
      title: 'ประกัน',
      key: 'insurance',
      width: 180,
      render: (_, record) => {
        if (record.type === 'supplier') return <Tag color="gray">ไม่เกี่ยวข้อง</Tag>

        return (
          <div>
            {renderInsuranceStatus(record)}
            {record.insurance_end_date && (
              <div style={{ fontSize: '11px', color: '#666', marginTop: 2 }}>
                หมดอายุ: {dayjs(record.insurance_end_date).format('DD/MM/YYYY')}
              </div>
            )}
            {record.insurance_renewal_amount && (
              <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 1 }}>
                ค่าต่ออายุ: {record.insurance_renewal_amount.toLocaleString()} ฿
              </div>
            )}
          </div>
        )
      }
    },
    {
      title: 'ติดต่อ',
      key: 'contact_info',
      width: 200,
      render: (_, record) => (
        <div>
          {record.phone && (
            <div style={{ fontSize: '12px', marginBottom: 2 }}>
              <PhoneOutlined style={{ marginRight: 4, color: '#1890ff' }} />
              {record.phone}
            </div>
          )}
          {record.email && (
            <div style={{ fontSize: '12px' }}>
              <MailOutlined style={{ marginRight: 4, color: '#52c41a' }} />
              {record.email}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'ที่อยู่',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
      render: (address) => address && (
        <div style={{ fontSize: '12px' }}>
          <HomeOutlined style={{ marginRight: 4, color: '#faad14' }} />
          {address}
        </div>
      )
    },
    {
      title: 'การจัดการ',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewContact(record)}
            size="small"
            title="ดูรายละเอียด"
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditContact(record)}
            size="small"
            title="แก้ไข"
          />
          {record.type !== 'supplier' && record.insurance_end_date && (
            <Button
              type="link"
              icon={<ReloadOutlined />}
              onClick={() => handleRenewInsurance(record)}
              size="small"
              title="ต่ออายุประกัน"
              style={{ color: '#52c41a' }}
            />
          )}
          <Popconfirm
            title="คุณแน่ใจว่าต้องการลบผู้ติดต่อนี้?"
            onConfirm={() => handleDeleteContact(record.id)}
            okText="ใช่"
            cancelText="ไม่"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
              title="ลบ"
            />
          </Popconfirm>
        </Space>
      )
    }
  ]

  // Mobile card renderer for contact list
  const renderMobileContactCard = (contact) => {
    const typeData = contactTypes.find(t => t.value === contact.type)

    return (
      <Card
        key={contact.id}
        size="small"
        style={{ marginBottom: 8 }}
        actions={[
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewContact(contact)}
            size="small"
          >
            ดู
          </Button>,
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditContact(contact)}
            size="small"
          >
            แก้ไข
          </Button>,
          contact.type !== 'supplier' && contact.insurance_end_date ? (
            <Button
              type="link"
              icon={<ReloadOutlined />}
              onClick={() => handleRenewInsurance(contact)}
              size="small"
              style={{ color: '#52c41a' }}
            >
              ต่ออายุ
            </Button>
          ) : (
            <Popconfirm
              title="ลบผู้ติดต่อนี้?"
              onConfirm={() => handleDeleteContact(contact.id)}
              okText="ใช่"
              cancelText="ไม่"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                size="small"
              >
                ลบ
              </Button>
            </Popconfirm>
          )
        ]}
      >
        <Card.Meta
          avatar={
            <Avatar
              size="large"
              style={{
                backgroundColor: typeData?.color === 'blue' ? '#1890ff' :
                                typeData?.color === 'green' ? '#52c41a' : '#722ed1'
              }}
            >
              {typeData?.icon}
            </Avatar>
          }
          title={
            <div>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{contact.name}</div>
              {contact.company_name && (
                <div style={{ fontSize: 12, color: '#666', fontWeight: 'normal' }}>
                  {contact.company_name}
                </div>
              )}
            </div>
          }
          description={
            <div>
              <div style={{ marginBottom: 8 }}>
                <Tag color={typeData?.color} size="small">
                  {typeData?.label}
                </Tag>
                {contact.type !== 'supplier' && (
                  <div style={{ marginTop: 4 }}>
                    {renderInsuranceStatus(contact)}
                  </div>
                )}
              </div>
              <div style={{ marginTop: 8 }}>
                {contact.phone && (
                  <div style={{ fontSize: 12, marginBottom: 2 }}>
                    <PhoneOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                    {contact.phone}
                  </div>
                )}
                {contact.email && (
                  <div style={{ fontSize: 12, marginBottom: 2 }}>
                    <MailOutlined style={{ marginRight: 6, color: '#52c41a' }} />
                    {contact.email}
                  </div>
                )}
                {contact.insurance_end_date && contact.type !== 'supplier' && (
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>
                    <CalendarOutlined style={{ marginRight: 6, color: '#faad14' }} />
                    หมดอายุ: {dayjs(contact.insurance_end_date).format('DD/MM/YYYY')}
                  </div>
                )}
                {contact.insurance_renewal_amount && (
                  <div style={{ fontSize: 12, color: '#1890ff', marginBottom: 2 }}>
                    <DollarOutlined style={{ marginRight: 6 }} />
                    ค่าต่ออายุ: {contact.insurance_renewal_amount.toLocaleString()} ฿
                  </div>
                )}
                {contact.address && (
                  <div style={{ fontSize: 12, color: '#666' }}>
                    <HomeOutlined style={{ marginRight: 6, color: '#faad14' }} />
                    {contact.address.length > 50 ? `${contact.address.substring(0, 50)}...` : contact.address}
                  </div>
                )}
              </div>
            </div>
          }
        />
      </Card>
    )
  }

  // Form fields component for reuse in both Modal and Drawer
  const FormFields = () => (
    <>
      <Row gutter={isMobile ? 0 : 16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="name"
            label="ชื่อ"
            rules={[{ required: true, message: 'กรุณาใส่ชื่อ' }]}
          >
            <Input placeholder="ชื่อผู้ติดต่อ" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="type"
            label="ประเภท"
            rules={[{ required: true, message: 'กรุณาเลือกประเภท' }]}
          >
            <Select placeholder="เลือกประเภท">
              {contactTypes.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={isMobile ? 0 : 16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="company_name"
            label="ชื่อบริษัท"
          >
            <Input placeholder="ชื่อบริษัท" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="tax_id"
            label="เลขประจำตัวผู้เสียภาษี"
          >
            <Input placeholder="เลขประจำตัวผู้เสียภาษี" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={isMobile ? 0 : 16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="phone"
            label="เบอร์โทรศัพท์"
            rules={[{ required: false , message: 'จำเป็นต้องกรอกเบอร์โทรศัพท์' }]}
          >
            <Input placeholder="เบอร์โทรศัพท์" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="email"
            label="อีเมล"
            rules={[{ type: 'email', message: 'รูปแบบอีเมลไม่ถูกต้อง' }]}
          >
            <Input placeholder="อีเมล" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={isMobile ? 0 : 16}>
        <Col xs={24} md={18}>
          <Form.Item
            name="address"
            label="ที่อยู่"
          >
            <Input.TextArea
              rows={3}
              placeholder="ที่อยู่..."
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item
            name="postal_code"
            label="รหัสไปรษณีย์"
          >
            <Input placeholder="รหัสไปรษณีย์" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="contact_person"
        label="ผู้ติดต่อ"
      >
        <Input placeholder="ชื่อผู้ติดต่อ" />
      </Form.Item>

      <Form.Item
        name="notes"
        label="หมายเหตุ"
      >
        <Input.TextArea
          rows={3}
          placeholder="หมายเหตุเพิ่มเติม..."
        />
      </Form.Item>

      <Divider orientation="left">
        <DollarOutlined /> การผูกผังบัญชี
      </Divider>

      <Row gutter={isMobile ? 0 : 16}>
        <Col xs={24} md={12}>
          <Form.Item name="receivable_account_id" label="บัญชีลูกหนี้ของลูกค้า">
            <Select allowClear showSearch optionFilterProp="label" options={accountOptions} placeholder="ใช้บัญชีลูกหนี้ค่าเริ่มต้น" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="payable_account_id" label="บัญชีเจ้าหนี้ของผู้ขาย">
            <Select allowClear showSearch optionFilterProp="label" options={accountOptions} placeholder="ใช้บัญชีเจ้าหนี้ค่าเริ่มต้น" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="revenue_account_id" label="บัญชีรายได้เฉพาะลูกค้า">
            <Select allowClear showSearch optionFilterProp="label" options={accountOptions} placeholder="ใช้บัญชีรายได้ค่าเริ่มต้น" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="expense_account_id" label="บัญชีค่าใช้จ่ายเฉพาะผู้ขาย">
            <Select allowClear showSearch optionFilterProp="label" options={accountOptions} placeholder="ใช้บัญชีค่าใช้จ่ายค่าเริ่มต้น" />
          </Form.Item>
        </Col>
      </Row>

      {/* Insurance Section */}
      <Divider orientation="left">
        <SafetyOutlined /> ข้อมูลประกันซอฟแวร์
      </Divider>

      <Row gutter={isMobile ? 0 : 16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="insurance_start_date"
            label="วันเริ่มต้นประกัน"
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="เลือกวันเริ่มต้น"
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="insurance_end_date"
            label="วันหมดอายุประกัน"
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="เลือกวันหมดอายุ"
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={isMobile ? 0 : 16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="insurance_renewal_amount"
            label="ค่าต่ออายุประกัน (บาท)"
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="จำนวนเงินค่าต่ออายุ"
              min={0}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="insurance_notes"
        label="หมายเหตุประกัน"
      >
        <Input.TextArea
          rows={2}
          placeholder="หมายเหตุเกี่ยวกับประกัน..."
        />
      </Form.Item>
    </>
  )

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <Title level={2} style={{ fontSize: isMobile ? 20 : 24, marginBottom: 16 }}>
        <ContactsOutlined /> จัดการผู้ติดต่อ
      </Title>

      {/* Insurance Expiring Alert */}
      {expiringContacts.length > 0 && (
        <Alert
          message={`แจ้งเตือน: มีประกันของลูกค้า ${expiringContacts.length} รายการ ที่จะหมดอายุภายใน 30 วัน`}
          description={
            <div style={{ marginTop: 8 }}>
              {expiringContacts.slice(0, 3).map(contact => (
                <div key={contact.id} style={{ marginBottom: 4 }}>
                  • {contact.name} หมดอายุ {dayjs(contact.insurance_end_date).format('DD/MM/YYYY')}
                  (เหลือ {contact.days_remaining} วัน)
                </div>
              ))}
              {expiringContacts.length > 3 && (
                <div style={{ color: '#1890ff', cursor: 'pointer' }}
                     onClick={() => handleInsuranceStatusFilter('expiring_soon')}>
                  ดูทั้งหมด {expiringContacts.length} รายการ
                </div>
              )}
            </div>
          }
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Statistics Cards */}
      <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size={isMobile ? "small" : "default"}>
            <Statistic
              title="ทั้งหมด"
              value={totalContacts}
              prefix={<ContactsOutlined />}
              valueStyle={{ fontSize: isMobile ? 16 : 24 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            onClick={() => handleTypeFilter('customer')}
            style={{ cursor: 'pointer' }}
            size={isMobile ? "small" : "default"}
          >
            <Statistic
              title="ลูกค้า"
              value={customerCount}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff', fontSize: isMobile ? 16 : 24 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            onClick={() => handleTypeFilter('supplier')}
            style={{ cursor: 'pointer' }}
            size={isMobile ? "small" : "default"}
          >
            <Statistic
              title="ซัพพลายเออร์"
              value={supplierCount}
              prefix={<ShopOutlined />}
              valueStyle={{ color: '#52c41a', fontSize: isMobile ? 16 : 24 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            onClick={() => handleInsuranceStatusFilter('expiring_soon')}
            style={{ cursor: 'pointer' }}
            size={isMobile ? "small" : "default"}
          >
            <Statistic
              title="ใกล้หมดอายุ"
              value={insuranceStatusCounts.expiring_soon || 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#fa8c16', fontSize: isMobile ? 16 : 24 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Insurance Status Statistics */}
      <Card size={isMobile ? "small" : "default"} style={{ marginBottom: 16 }}>
        <Title level={4} style={{ fontSize: isMobile ? 16 : 18, marginBottom: 12 }}>
          <SafetyOutlined /> สถานะประกันซอฟแวร์
        </Title>
        <Row gutter={[8, 8]}>
          <Col xs={12} sm={6}>
            <Card
              size="small"
              onClick={() => handleInsuranceStatusFilter('active')}
              style={{ cursor: 'pointer', textAlign: 'center' }}
            >
              <Statistic
                title="ใช้งานได้"
                value={insuranceStatusCounts.active || 0}
                valueStyle={{ color: '#52c41a', fontSize: isMobile ? 14 : 20 }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              size="small"
              onClick={() => handleInsuranceStatusFilter('expiring_soon')}
              style={{ cursor: 'pointer', textAlign: 'center' }}
            >
              <Statistic
                title="ใกล้หมดอายุ"
                value={insuranceStatusCounts.expiring_soon || 0}
                valueStyle={{ color: '#fa8c16', fontSize: isMobile ? 14 : 20 }}
                prefix={<WarningOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              size="small"
              onClick={() => handleInsuranceStatusFilter('expired')}
              style={{ cursor: 'pointer', textAlign: 'center' }}
            >
              <Statistic
                title="หมดอายุแล้ว"
                value={insuranceStatusCounts.expired || 0}
                valueStyle={{ color: '#f5222d', fontSize: isMobile ? 14 : 20 }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              size="small"
              onClick={() => handleInsuranceStatusFilter('not_applicable')}
              style={{ cursor: 'pointer', textAlign: 'center' }}
            >
              <Statistic
                title="ไม่มีประกัน"
                value={insuranceStatusCounts.not_applicable || 0}
                valueStyle={{ color: '#8c8c8c', fontSize: isMobile ? 14 : 20 }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Search and Filters */}
      <Card style={{ marginBottom: 16 }} size={isMobile ? "small" : "default"}>
        {isMobile ? (
          // Mobile layout
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Search
              placeholder="ค้นหาผู้ติดต่อ..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFilterDrawerVisible(true)}
              >
                ตัวกรอง
                {(selectedType || selectedInsuranceStatus) && <Badge dot style={{ marginLeft: 4 }} />}
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddContact}>
                เพิ่มผู้ติดต่อ
              </Button>
            </Space>
          </Space>
        ) : (
          // Desktop layout
          <Row gutter={16} align="middle">
            <Col xs={24} sm={6}>
              <Search
                placeholder="ค้นหาผู้ติดต่อ..."
                allowClear
                onSearch={handleSearch}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={4}>
              <Select
                value={selectedType}
                onChange={handleTypeFilter}
                style={{ width: '100%' }}
                placeholder="ประเภท"
                allowClear
              >
                {contactTypes.map(type => (
                  <Option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={5}>
              <Select
                value={selectedInsuranceStatus}
                onChange={handleInsuranceStatusFilter}
                style={{ width: '100%' }}
                placeholder="สถานะประกัน"
                allowClear
              >
                {insuranceStatusTypes.map(status => (
                  <Option key={status.value} value={status.value}>
                    {status.icon} {status.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={9} style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddContact}
              >
                เพิ่มผู้ติดต่อใหม่
              </Button>
            </Col>
          </Row>
        )}
      </Card>

      {/* Contacts List */}
      <Card size={isMobile ? "small" : "default"}>
        {isMobile ? (
          // Mobile card view
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>กำลังโหลด...</div>
            ) : contacts && contacts.length > 0 ? (
              <>
                {contacts.map(renderMobileContactCard)}
                {/* Mobile Pagination */}
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <Button
                    disabled={pagination.current === 1}
                    onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
                    style={{ marginRight: 8 }}
                  >
                    ก่อนหน้า
                  </Button>
                  <span style={{ margin: '0 16px' }}>
                    {pagination.current} / {Math.ceil(pagination.total / pagination.pageSize)}
                  </span>
                  <Button
                    disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                    onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
                  >
                    ถัดไป
                  </Button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                ไม่พบข้อมูลผู้ติดต่อ
              </div>
            )}
          </div>
        ) : (
          // Desktop table view
          <Table
            columns={columns}
            dataSource={contacts || []}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1200 }}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} จาก ${total} รายการ`
            }}
            onChange={handleTableChange}
          />
        )}
      </Card>

      {/* Floating Action Button for Mobile */}
      {isMobile && (
        <FloatButton
          icon={<PlusOutlined />}
          type="primary"
          style={{ right: 24, bottom: 24 }}
          onClick={handleAddContact}
        />
      )}

      {/* Mobile Filter Drawer */}
      <Drawer
        title="ตัวกรองข้อมูล"
        placement="bottom"
        height="auto"
        open={filterDrawerVisible}
        onClose={() => setFilterDrawerVisible(false)}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>ประเภทผู้ติดต่อ:</div>
            <Space wrap>
              <Button
                type={selectedType === '' ? 'primary' : 'default'}
                onClick={() => handleTypeFilter('')}
              >
                ทั้งหมด
              </Button>
              {contactTypes.map(type => (
                <Button
                  key={type.value}
                  type={selectedType === type.value ? 'primary' : 'default'}
                  icon={type.icon}
                  onClick={() => handleTypeFilter(type.value)}
                >
                  {type.label}
                </Button>
              ))}
            </Space>
          </div>

          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>สถานะประกัน:</div>
            <Space wrap>
              <Button
                type={selectedInsuranceStatus === '' ? 'primary' : 'default'}
                onClick={() => handleInsuranceStatusFilter('')}
              >
                ทั้งหมด
              </Button>
              {insuranceStatusTypes.map(status => (
                <Button
                  key={status.value}
                  type={selectedInsuranceStatus === status.value ? 'primary' : 'default'}
                  icon={status.icon}
                  onClick={() => handleInsuranceStatusFilter(status.value)}
                >
                  {status.label}
                </Button>
              ))}
            </Space>
          </div>
        </Space>
      </Drawer>

      {/* Add/Edit Contact Modal/Drawer */}
      {isMobile ? (
        <Drawer
          title={editingContact ? 'แก้ไขผู้ติดต่อ' : 'เพิ่มผู้ติดต่อใหม่'}
          placement="right"
          width="100%"
          open={modalVisible}
          onClose={handleModalCancel}
          extra={
            <Space>
              <Button onClick={handleModalCancel}>ยกเลิก</Button>
              <Button type="primary" onClick={handleOk}>
                บันทึก
              </Button>
            </Space>
          }
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleModalOk}
            initialValues={{
              type: 'customer'
            }}
          >
            <FormFields />
          </Form>
        </Drawer>
      ) : (
        <Modal
          title={editingContact ? 'แก้ไขผู้ติดต่อ' : 'เพิ่มผู้ติดต่อใหม่'}
          open={modalVisible}
          onOk={handleOk}
          onCancel={handleModalCancel}
          width={900}
          okText="บันทึก"
          cancelText="ยกเลิก"
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleModalOk}
            initialValues={{
              type: 'customer'
            }}
          >
            <FormFields />
          </Form>
        </Modal>
      )}

      {/* Insurance Renewal Modal */}
      <Modal
        title={`ต่ออายุประกันซอฟแวร์ - ${renewingContact?.name}`}
        open={renewalModalVisible}
        onOk={handleRenewalSubmit}
        onCancel={handleRenewalCancel}
        width={600}
        okText="ต่ออายุ"
        cancelText="ยกเลิก"
      >
        <Form
          form={renewalForm}
          layout="vertical"
          onFinish={handleRenewalOk}
        >
          {renewingContact && (
            <Alert
              message="ข้อมูลประกันปัจจุบัน"
              description={
                <div>
                  <div>วันเริ่มต้น: {renewingContact.insurance_start_date ? dayjs(renewingContact.insurance_start_date).format('DD/MM/YYYY') : 'ไม่ระบุ'}</div>
                  <div>วันหมดอายุ: {renewingContact.insurance_end_date ? dayjs(renewingContact.insurance_end_date).format('DD/MM/YYYY') : 'ไม่ระบุ'}</div>
                  <div>ค่าต่ออายุปัจจุบัน: {renewingContact.insurance_renewal_amount?.toLocaleString() || 0} ฿</div>
                </div>
              }
              type="info"
              style={{ marginBottom: 16 }}
            />
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="new_start_date"
                label="วันเริ่มต้นใหม่"
                rules={[{ required: true, message: 'กรุณาเลือกวันเริ่มต้น' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="เลือกวันเริ่มต้น"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="new_end_date"
                label="วันหมดอายุใหม่"
                rules={[{ required: true, message: 'กรุณาเลือกวันหมดอายุ' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="เลือกวันหมดอายุ"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="renewal_amount"
            label="ค่าต่ออายุประกัน (บาท)"
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="จำนวนเงินค่าต่ออายุ"
              min={0}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="หมายเหตุการต่ออายุ"
          >
            <Input.TextArea
              rows={3}
              placeholder="หมายเหตุเกี่ยวกับการต่ออายุครั้งนี้..."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Contact Detail Modal/Drawer */}
      {isMobile ? (
        <Drawer
          title={`รายละเอียดผู้ติดต่อ - ${viewingContact?.name || ''}`}
          placement="right"
          width="100%"
          open={detailModalVisible}
          onClose={() => setDetailModalVisible(false)}
          bodyStyle={{ paddingBottom: 96 }}
        >
          {viewingContact && (
            <Tabs
              defaultActiveKey="dashboard"
              size="small"
              items={[
                {
                  key: 'dashboard',
                  label: <span><DollarOutlined />ภาพรวมซื้อ–ขาย</span>,
                  children: <ContactDashboard contactId={viewingContact.id} />
                },
                {
                  key: 'info',
                  label: <span><ContactsOutlined />ข้อมูลผู้ติดต่อ</span>,
                  children: <ContactProfileDetails
                    contact={viewingContact}
                    contactTypes={contactTypes}
                    accountOptions={accountOptions}
                    renderInsuranceStatus={renderInsuranceStatus}
                    onEdit={() => { setDetailModalVisible(false); handleEditContact(viewingContact) }}
                    onRenewInsurance={() => handleRenewInsurance(viewingContact)}
                  />
                }
              ]}
            />
          )}
        </Drawer>
      ) : (
        <Modal
          title={`รายละเอียดผู้ติดต่อ - ${viewingContact?.name || ''}`}
          open={detailModalVisible}
          onCancel={() => setDetailModalVisible(false)}
          width={1200}
          footer={<Button onClick={() => setDetailModalVisible(false)}>ปิด</Button>}
          destroyOnClose
        >
          {viewingContact && (
            <Tabs
              defaultActiveKey="dashboard"
              items={[
                {
                  key: 'dashboard',
                  label: <span><DollarOutlined />ภาพรวมซื้อ–ขาย</span>,
                  children: <ContactDashboard contactId={viewingContact.id} />
                },
                {
                  key: 'info',
                  label: <span><ContactsOutlined />ข้อมูลผู้ติดต่อ</span>,
                  children: <ContactProfileDetails
                    contact={viewingContact}
                    contactTypes={contactTypes}
                    accountOptions={accountOptions}
                    renderInsuranceStatus={renderInsuranceStatus}
                    onEdit={() => { setDetailModalVisible(false); handleEditContact(viewingContact) }}
                    onRenewInsurance={() => handleRenewInsurance(viewingContact)}
                  />
                }
              ]}
            />
          )}
        </Modal>
      )}
    </div>
  )
}

export default ContactsPage
